import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CaseDetailModal from './CaseDetailModal';
import { cases } from '../../../mocks/cases';

interface CaseListProps {
  selectedTypes: string[];
}

const CaseList = ({ selectedTypes }: CaseListProps) => {
  const [selectedCase, setSelectedCase] = useState<string | null>(null);

  const filteredCases = useMemo(() => {
    if (selectedTypes.length === 0) {
      return cases;
    }
    return cases.filter(c => selectedTypes.includes(c.type));
  }, [selectedTypes]);

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 py-16">
        {filteredCases.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-6xl text-neutral-300 mb-4"></i>
            <p className="text-lg text-neutral-500">
              暂无符合条件的案例，请调整筛选条件
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-sm text-neutral-600">
                找到 <span className="font-bold text-[#1A3A5C]">{filteredCases.length}</span> 个相关案例
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map((caseItem, index) => (
                <motion.div
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => setSelectedCase(caseItem.id)}
                >
                  {/* 类型标签 */}
                  <div className="bg-gradient-to-r from-[#1A3A5C] to-[#2A4A6C] px-6 py-4">
                    <div className="flex items-center gap-2">
                      <i className={`${caseItem.icon} text-[#FFB800] text-xl`}></i>
                      <span className="text-white font-bold">{caseItem.typeLabel}</span>
                    </div>
                  </div>

                  {/* 案例内容 */}
                  <div className="p-6">
                    {/* 背景说明 */}
                    <p className="text-sm text-neutral-700 mb-4 line-clamp-2">
                      {caseItem.background}
                    </p>

                    {/* 关键原因 */}
                    <div className="bg-[#FFB800]/10 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <i className="ri-lightbulb-flash-line text-[#FFB800] text-lg mt-0.5 flex-shrink-0"></i>
                        <div>
                          <p className="text-xs text-neutral-600 mb-1">使用 1039 的关键原因</p>
                          <p className="text-sm font-bold text-[#1A3A5C]">
                            {caseItem.keyReason}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 结果关键词 */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {caseItem.results.map((result, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full whitespace-nowrap"
                        >
                          <i className="ri-checkbox-circle-line"></i>
                          {result}
                        </span>
                      ))}
                    </div>

                    {/* 查看详情 */}
                    <button className="w-full py-2.5 border-2 border-[#1A3A5C] text-[#1A3A5C] rounded-lg font-bold hover:bg-[#1A3A5C] hover:text-white transition-colors group-hover:border-[#FFB800] group-hover:text-[#FFB800] group-hover:hover:bg-[#FFB800] group-hover:hover:text-white whitespace-nowrap">
                      查看详细案例
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* 案例详情弹窗 */}
      <AnimatePresence>
        {selectedCase && (
          <CaseDetailModal
            caseId={selectedCase}
            onClose={() => setSelectedCase(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CaseList;